import {
    Box,
    Text,
    Badge,
    Flex,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    VStack,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { formatTime } from "../../../utils/date";

import fire1 from "../../../assets/fire1.png";
import fire2 from "../../../assets/fire2.png";
import iki3 from "../../../assets/iki3.png";
import iki4 from "../../../assets/iki4.png";

const STOVE_IMAGES = [iki4, fire1, iki3, fire2];

export const MemberSession = ({
    slot,
    name,
    handleSelectSession,
    customerId,
    setCancelConfirmationModalOpen,
    setSessionToCancel,
    setRedirect,
    index,
}) => {
    const customerAlreadyBooked = customerAlreadyBookedSession(
        customerId,
        slot
    );
    const customerAlreadyWaitlisted = customerAlreadyWaitlistedSession(
        customerId,
        slot
    );

    const handleCancelBooking = (e) => {
        e.stopPropagation();
        setCancelConfirmationModalOpen(true);
        setSessionToCancel(slot);
    };

    const handleBuyMorePasses = (e) => {
        e.stopPropagation();
        setRedirect(true);
    };

    return (
        <Box
            key={`${slot.startHour}-${slot.endHour}`}
            p={4}
            position="relative"
            bg="linear-gradient(135deg, rgba(0, 0, 30, 1) 0%, rgba(55, 1, 25, 1) 100%)"
            borderWidth="4px"
            borderRadius="2xl"
            borderColor="gray.200"
            minHeight="140px"
            shadow="lg"
            cursor={
                (slot.status === "AVAILABLE" || slot.status === "WAITLIST") &&
                !customerAlreadyBooked &&
                !customerAlreadyWaitlisted
                    ? "pointer"
                    : "default"
            }
            backgroundImage={`url(${
                STOVE_IMAGES[index % STOVE_IMAGES.length]
            })`}
            backgroundSize="cover"
            backgroundPosition="center"
            onClick={() => {
                if (!customerAlreadyBooked && !customerAlreadyWaitlisted) {
                    handleSelectSession(slot);
                }
            }}
        >
            <Flex justify="space-between" align="center">
                <VStack align="flex-start" spacing={0.5}>
                    <Text fontWeight="bold" fontSize="md">
                        {formatTime(slot.startHour)} -{" "}
                        {formatTime(slot.endHour)}
                    </Text>
                    <Text fontWeight="medium" fontSize="sm" color="gray.300">
                        {name}
                    </Text>
                </VStack>
                {customerAlreadyBooked && (
                    <AlreadyBookedMenu
                        customerAlreadyBooked={customerAlreadyBooked}
                        handleBuyMorePasses={handleBuyMorePasses}
                        handleCancelBooking={handleCancelBooking}
                        customerAlreadyWaitlisted={customerAlreadyWaitlisted}
                        slot={slot}
                    />
                )}
            </Flex>
            <SessionStatusText
                customerAlreadyBooked={customerAlreadyBooked}
                customerAlreadyWaitlisted={customerAlreadyWaitlisted}
                slot={slot}
            />
            <BadgeComponent
                customerAlreadyBooked={customerAlreadyBooked}
                customerAlreadyWaitlisted={customerAlreadyWaitlisted}
                slot={slot}
            />
        </Box>
    );
};

// Updated BadgeComponent with more professional styling
const BadgeComponent = ({
    customerAlreadyBooked,
    customerAlreadyWaitlisted,
    slot,
}) => {
    // Shared style properties for a more refined badge appearance.
    const badgeProps = {
        fontSize: "xs", // small font for a neat look
        px: 2, // horizontal padding
        py: 1, // vertical padding
        fontWeight: "semibold", // balanced emphasis
        borderRadius: "full", // fully rounded edges
        letterSpacing: "wider", // increased spacing for a cleaner feel
        textTransform: "uppercase", // uppercase text for consistency
        my: 2, // vertical margin
    };

    let badgeColor = "green";
    let badgeText = "Available";

    if (customerAlreadyBooked) {
        badgeColor = "blue";
        badgeText = "Booked";
        return (
            <Badge colorScheme={badgeColor} {...badgeProps}>
                {badgeText}
            </Badge>
        );
    }

    if (slot.spotsRemaining === 0) {
        badgeColor = "yellow";
        badgeText = "Waitlist Available";

        if (customerAlreadyWaitlisted) {
            badgeColor = "purple";
            badgeText = "Waitlisted";
        }
    }

    return (
        <Badge colorScheme={badgeColor} {...badgeProps}>
            {badgeText}
        </Badge>
    );
};

const AlreadyBookedMenu = ({
    customerAlreadyBooked,
    handleCancelBooking,
    handleBuyMorePasses,
    customerAlreadyWaitlisted,
    slot,
}) => {
    return (
        <Menu>
            <MenuButton
                as={IconButton}
                icon={<HamburgerIcon />}
                variant="ghost"
                size="sm"
                onClick={(e) => e.stopPropagation()}
            />
            <MenuList>
                {customerAlreadyBooked && (
                    <>
                        <MenuItem onClick={handleCancelBooking}>
                            Cancel Booking
                        </MenuItem>
                        {slot.spotsRemaining > 1 && (
                            <MenuItem onClick={handleBuyMorePasses}>
                                Buy More Passes
                            </MenuItem>
                        )}
                    </>
                )}
                {customerAlreadyWaitlisted && (
                    <MenuItem onClick={handleCancelBooking}>
                        Leave Waitlist
                    </MenuItem>
                )}
            </MenuList>
        </Menu>
    );
};

const SessionStatusText = ({
    slot,
    customerId,
    customerAlreadyWaitlisted,
    customerAlreadyBooked,
}) => {
    // If there are available spots, show that first
    if (slot.spotsRemaining !== 0) {
        return (
            <Text fontWeight="medium" fontSize="sm" color="gray.300">
                {slot.spotsRemaining} left
            </Text>
        );
    }

    // When spots are not available, check if waitlist is applicable for non-waitlisted customers
    if (
        slot.spotsRemaining === 0 &&
        slot.waitlistSpotsRemaining > 0 &&
        !customerAlreadyWaitlisted &&
        !customerAlreadyBooked
    ) {
        return (
            <Text fontWeight="medium" fontSize="sm" color="gray.300">
                {slot.waitlistSpotsRemaining} waitlist spots left
            </Text>
        );
    }

    // If the customer is already waitlisted, display their waitlist position
    if (customerAlreadyWaitlisted) {
        return (
            <Text fontWeight="medium" fontSize="sm" color="gray.300">
                Waitlist Position:{" "}
                <strong>{getWaitlistPosition(customerId, slot)}</strong>
            </Text>
        );
    }

    return null;
};

const customerAlreadyBookedSession = (customerId, session) => {
    return session.confirmedSet.some(
        (confirmedSetItem) => confirmedSetItem.customerId === customerId
    );
};

const customerAlreadyWaitlistedSession = (customerId, session) => {
    return session.waitlistSet.some(
        (waitlistSetItem) => waitlistSetItem.customerId === customerId
    );
};

const getWaitlistPosition = (customerId, session) => {
    const customerWaitlistMetadata = session.waitlistSet.find(
        (waitlistSetItem) => waitlistSetItem.customerId === customerId
    );
    return customerWaitlistMetadata.waitlistPosition;
};
